import React, { useCallback, useEffect, useState } from 'react';
import '../index.css';
import { useAuth } from '../context/authContext';
import { getOrganisation } from '../services/organisation.service';
import { getUsers } from '../services/user.service';
import { getWorkflows } from '../services/workflow.service';
import ApprovalProcessCard from '../components/workflows/ApprovalProcessCard';
import UpdateAuthCount from '../components/users/UpdateAuthCount';
import {
    ApprovalProcessCardSkeleton,
    TableCardSkeleton,
} from '../components/ui/card-skeletons';
import AsyncContent from '../components/ui/AsyncContent';
import WorkflowsList from '../components/workflows/WorkflowsList';

const WorkflowsPage = () => {

    const { user } = useAuth();
    const [organisation, setOrganisation] = useState(null);
    const [users, setUsers] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [authCount, setAuthCount] = useState(1);
    const [restrictSubmitterAuth, setRestrictSubmitterAuth] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);

    const triggerReload = useCallback(() => setReload((prev) => !prev), []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const [orgRes, usersRes, workflowsRes] = await Promise.all([
                getOrganisation(user.organisationId, user.token),
                getUsers(user.token, user.organisationId),
                getWorkflows(user.token, user.organisationId),
            ]);

            if (orgRes && orgRes.status === 200) {
                const org = orgRes.data.data;
                setOrganisation(org);
                setAuthCount(org.requiredAuthorisations);
                setRestrictSubmitterAuth(org.restrictSubmitterAuth);
            }
            if (usersRes && usersRes.status === 200) setUsers(usersRes.data.data);
            if (workflowsRes && workflowsRes.status === 200) setWorkflows(workflowsRes.data.data);
            setLoading(false);
        };

        fetchData();
    }, [user.token, user.organisationId, reload]);

    const savedProcessType = organisation?.approvalProcessType || 'STANDARD';
    const isStandard = savedProcessType === 'STANDARD';

    return (
        <div className="App">
            <div className="max-w-7xl mx-auto flex flex-col gap-8">
                <div>
                    <h2 className="mb-1">Approval Workflows</h2>
                    <p className="text-gray-500">
                        Configure how your organisation approves payments using either the standard
                        count-based process or custom value-tiered workflows.
                    </p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                    <div className="xl:col-span-1 flex flex-col gap-8">
                        <AsyncContent
                            loading={loading}
                            skeleton={<ApprovalProcessCardSkeleton />}
                        >
                            <ApprovalProcessCard
                                approvalProcessType={organisation?.approvalProcessType}
                                restrictSubmitterAuth={organisation?.restrictSubmitterAuth}
                                onUpdated={triggerReload}
                            />
                        </AsyncContent>
                    </div>
                    <div className="xl:col-span-2">
                        <AsyncContent
                            loading={loading}
                            skeleton={<TableCardSkeleton titleWidth="w-40" rows={5} cols={5} />}
                        >
                            {isStandard ? (
                                <UpdateAuthCount
                                    users={users}
                                    authCount={authCount}
                                    setAuthCount={setAuthCount}
                                    restrictSubmitterAuth={restrictSubmitterAuth}
                                    setRestrictSubmitterAuth={setRestrictSubmitterAuth}
                                />
                            ) : (
                                <WorkflowsList workflows={workflows} onChanged={triggerReload} />
                            )}
                        </AsyncContent>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkflowsPage;
